import type { StaffListItem } from "@vaulted/shared";
import { useQuery } from "@tanstack/react-query";
import { get } from "../../api";
import { Checkbox, Field, MoneyField, Panel, PanelHeader, TextAreaField } from "../../components/ui";
import { AddressField } from "../../components/AddressField";
import { AddButton, NumberInput, RemoveButton, SettingsForm, type SettingsResponse, useDraft } from "./common";

const KEYS = [
  "mobile.leadTimeMinutes",
  "mobile.manualConfirmation",
  "mobile.locationTypes",
  "mobile.serviceAreaBands",
  "mobile.serviceAreaCenters",
  "mobile.minGuests",
  "mobile.travelFeeMaxCents",
  "mobile.nursesByGroupSize",
  "mobile.acknowledgmentText",
  "mobile.travelBeforeMinutes",
  "mobile.travelAfterMinutes",
  "mobile.maxAdvanceDays",
  "mobile.escalation",
  "mobile.escalationStaffIds",
] as const;

const keyFor = (label: string, taken: string[]) => {
  const base = label.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 30) || "type";
  let key = base;
  for (let i = 2; taken.includes(key); i++) key = `${base}_${i}`;
  return key;
};

export function MobileTab({ data }: { data: SettingsResponse }) {
  const d = useDraft(data.settings, KEYS, "Mobile visit rules saved.");
  const v = d.value;
  const types = v["mobile.locationTypes"];
  const bands = v["mobile.serviceAreaBands"];
  const centers = v["mobile.serviceAreaCenters"];
  const nurses = v["mobile.nursesByGroupSize"];
  const esc = v["mobile.escalation"];
  const setEsc = (patch: Partial<typeof esc>) => d.set("mobile.escalation", { ...esc, ...patch });
  const staff = useQuery({ queryKey: ["staff"], queryFn: () => get<StaffListItem[]>("/api/staff"), retry: false });

  return (
    <Panel>
      <PanelHeader title="Mobile visits" description="Rules for visits at a client's address." />
      <SettingsForm onSave={d.save} saving={d.saving} error={d.error} notice={d.notice} dirty={d.dirty}>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Minimum notice (hours)"
            type="number"
            min={0}
            max={168}
            step={0.25}
            value={v["mobile.leadTimeMinutes"] / 60}
            onChange={(e) => d.set("mobile.leadTimeMinutes", Math.round(Number(e.target.value) * 60))}
            hint="How far ahead a mobile visit must be requested."
          />
          <div className="pt-6">
            <Checkbox
              label="Confirm each request by hand"
              description="Requests wait for staff approval; the deposit is held until then."
              checked={v["mobile.manualConfirmation"]}
              onChange={(on) => d.set("mobile.manualConfirmation", on)}
            />
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Travel before (minutes)" type="number" min={0} max={240} step={5} value={v["mobile.travelBeforeMinutes"]} onChange={(e) => d.set("mobile.travelBeforeMinutes", Number(e.target.value))} hint="The nurse is busy this long before each visit." />
          <Field label="Travel after (minutes)" type="number" min={0} max={240} step={5} value={v["mobile.travelAfterMinutes"]} onChange={(e) => d.set("mobile.travelAfterMinutes", Number(e.target.value))} />
          <Field label="Book up to (days ahead)" type="number" min={1} max={365} value={v["mobile.maxAdvanceDays"]} onChange={(e) => d.set("mobile.maxAdvanceDays", Number(e.target.value))} />
        </div>

        <fieldset className="flex flex-col gap-3">
          <legend className="mb-1 text-sm font-semibold text-ink-soft">Unanswered requests</legend>
          <p className="text-sm text-muted">Nurses who are free then get a text right away, and again until someone answers. Then managers, then the people below. Last resort: the request is cancelled.</p>
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Text nurses again every (minutes)" type="number" min={5} max={240} value={esc.repeatMinutes} onChange={(e) => setEsc({ repeatMinutes: Number(e.target.value) })} />
            <Field label="Text managers after (minutes)" type="number" min={5} max={1440} value={esc.managerAfterMinutes} onChange={(e) => setEsc({ managerAfterMinutes: Number(e.target.value) })} />
            <Field label="…or this close to the visit (hours)" type="number" min={0} max={24} step={0.5} value={esc.managerBeforeVisitMinutes / 60} onChange={(e) => setEsc({ managerBeforeVisitMinutes: Math.round(Number(e.target.value) * 60) })} />
            <Field label="Text the people below after (minutes)" type="number" min={5} max={1440} value={esc.ownerAfterMinutes} onChange={(e) => setEsc({ ownerAfterMinutes: Number(e.target.value) })} />
            <Field label="…or this close to the visit (hours)" type="number" min={0} max={24} step={0.5} value={esc.ownerBeforeVisitMinutes / 60} onChange={(e) => setEsc({ ownerBeforeVisitMinutes: Math.round(Number(e.target.value) * 60) })} />
            <Field label="Cancel this close to the visit (hours)" type="number" min={0} max={24} step={0.25} value={esc.cancelBeforeVisitMinutes / 60} onChange={(e) => setEsc({ cancelBeforeVisitMinutes: Math.round(Number(e.target.value) * 60) })} />
          </div>
          {staff.data && (
            <div>
              <p className="mb-1 text-sm font-semibold text-ink-soft">Last people to text</p>
              <div className="flex flex-wrap gap-x-5 gap-y-2">
                {staff.data
                  .filter((m) => m.status === "active")
                  .map((m) => (
                    <Checkbox
                      key={m.id}
                      label={`${m.firstName} ${m.lastName}${m.phone ? "" : " (no mobile number)"}`}
                      checked={v["mobile.escalationStaffIds"].includes(m.id)}
                      onChange={(on) => d.set("mobile.escalationStaffIds", on ? [...v["mobile.escalationStaffIds"], m.id] : v["mobile.escalationStaffIds"].filter((x) => x !== m.id))}
                    />
                  ))}
              </div>
              <p className="mt-1 text-sm text-muted">None picked: everyone who can manage roles (owners).</p>
            </div>
          )}
        </fieldset>

        <fieldset className="flex flex-col gap-2">
          <legend className="mb-1 text-sm font-semibold text-ink-soft">Location types clients choose from</legend>
          <p className="text-sm text-muted">The nurse sees the type, and whether the client is new, before accepting.</p>
          {types.map((t, i) => (
            <div key={t.key} className="flex flex-col gap-2 rounded-[var(--radius-control)] border border-line p-3">
              <div className="flex flex-wrap items-center gap-3">
                <input
                  aria-label="Location type"
                  required
                  className="min-w-48 flex-1 rounded-[var(--radius-control)] border border-line bg-white px-2 py-1.5"
                  value={t.label}
                  onChange={(e) => d.set("mobile.locationTypes", types.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))}
                />
                <RemoveButton label="Remove location type" onClick={() => d.set("mobile.locationTypes", types.filter((_, j) => j !== i))} />
              </div>
              <TextAreaField
                label="Note clients must accept (optional)"
                rows={2}
                maxLength={1000}
                hint="Shown when they pick this; they tick that they understand. Leave empty for none."
                value={t.notice}
                onChange={(e) => d.set("mobile.locationTypes", types.map((x, j) => (j === i ? { ...x, notice: e.target.value } : x)))}
              />
            </div>
          ))}
          <AddButton
            onClick={() => d.set("mobile.locationTypes", [...types, { key: keyFor(`type ${types.length + 1}`, types.map((t) => t.key)), label: "", knownClientsOrGroupsOnly: false, notice: "" }])}
          >
            Add location type
          </AddButton>
        </fieldset>

        <fieldset className="flex flex-col gap-2">
          <legend className="mb-1 text-sm font-semibold text-ink-soft">Service area</legend>
          <p className="text-sm text-muted">
            Distance from the nearest center: the clinic, or one of the places below. Clients never see the distance. Beyond the last band, they're told it's outside the normal coverage area and may be
            declined, and can still send the request for staff to decide.
          </p>
          {centers.map((c, i) => (
            <div key={i} className="flex flex-wrap items-end gap-3">
              <Field className="w-40" label="Name" required maxLength={60} value={c.label} onChange={(e) => d.set("mobile.serviceAreaCenters", centers.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} />
              <AddressField
                className="min-w-64 flex-1"
                required
                value={c.address}
                onChange={(address) => d.set("mobile.serviceAreaCenters", centers.map((x, j) => (j === i ? { ...x, address } : x)))}
              />
              <span className="pb-1.5">
                <RemoveButton label="Remove center" onClick={() => d.set("mobile.serviceAreaCenters", centers.filter((_, j) => j !== i))} />
              </span>
            </div>
          ))}
          {centers.length < 5 && <AddButton onClick={() => d.set("mobile.serviceAreaCenters", [...centers, { label: "Home", address: "" }])}>Add a center (e.g. home)</AddButton>}
          <p className="mt-2 text-sm font-semibold text-ink-soft">Travel fee by distance</p>
          {bands.map((b, i) => (
            <div key={i} className="flex flex-wrap items-end gap-3 text-sm">
              <span className="pb-2">{i === 0 ? "Up to" : `${bands[i - 1]!.maxMiles} to`}</span>
              <span className="pb-1">
                <NumberInput label="Miles" value={b.maxMiles} min={1} max={500} onChange={(n) => d.set("mobile.serviceAreaBands", bands.map((x, j) => (j === i ? { ...x, maxMiles: n } : x)))} suffix="miles:" />
              </span>
              <MoneyField
                className="w-36"
                label="Travel fee"
                cents={b.feeCents}
                onChange={(c) => d.set("mobile.serviceAreaBands", bands.map((x, j) => (j === i ? { ...x, feeCents: c ?? 0 } : x)))}
              />
              {bands.length > 1 && (
                <span className="pb-1.5">
                  <RemoveButton label="Remove band" onClick={() => d.set("mobile.serviceAreaBands", bands.filter((_, j) => j !== i))} />
                </span>
              )}
            </div>
          ))}
          <AddButton onClick={() => d.set("mobile.serviceAreaBands", [...bands, { maxMiles: (bands[bands.length - 1]?.maxMiles ?? 0) + 10, feeCents: 0 }])}>Add band</AddButton>
        </fieldset>

        <Field
          className="max-w-64"
          label="Fewest guests per mobile booking"
          type="number"
          min={1}
          max={20}
          value={v["mobile.minGuests"]}
          onChange={(e) => d.set("mobile.minGuests", Math.max(1, Number(e.target.value) || 1))}
          hint="Online bookings with fewer are asked to add a guest or book at the clinic. Staff can book any size."
        />

        <MoneyField
          className="max-w-64"
          label="Largest travel fee staff can add"
          hint="Staff can add a travel fee when confirming, only after the client approves it."
          cents={v["mobile.travelFeeMaxCents"]}
          onChange={(c) => d.set("mobile.travelFeeMaxCents", c ?? 0)}
        />

        <fieldset className="flex flex-col gap-2">
          <legend className="mb-1 text-sm font-semibold text-ink-soft">Nurses needed</legend>
          {nurses.map((n, i) => (
            <div key={i} className="flex flex-wrap items-center gap-2 text-sm">
              {i === 0 ? "Up to" : `${nurses[i - 1]!.maxGuests + 1} to`}
              <NumberInput label="Guests" value={n.maxGuests} min={1} max={100} onChange={(x) => d.set("mobile.nursesByGroupSize", nurses.map((y, j) => (j === i ? { ...y, maxGuests: x } : y)))} suffix="guests:" />
              <NumberInput label="Nurses" value={n.nurses} min={1} max={20} onChange={(x) => d.set("mobile.nursesByGroupSize", nurses.map((y, j) => (j === i ? { ...y, nurses: x } : y)))} suffix="nurse(s)" />
              {nurses.length > 1 && <RemoveButton label="Remove row" onClick={() => d.set("mobile.nursesByGroupSize", nurses.filter((_, j) => j !== i))} />}
            </div>
          ))}
          <AddButton
            onClick={() => {
              const last = nurses[nurses.length - 1];
              d.set("mobile.nursesByGroupSize", [...nurses, { maxGuests: (last?.maxGuests ?? 0) + 4, nurses: (last?.nurses ?? 0) + 1 }]);
            }}
          >
            Add row
          </AddButton>
        </fieldset>

        <TextAreaField
          label="Location acknowledgment"
          hint="The client agrees to this when requesting a mobile visit."
          maxLength={2000}
          value={v["mobile.acknowledgmentText"]}
          onChange={(e) => d.set("mobile.acknowledgmentText", e.target.value)}
        />
      </SettingsForm>
    </Panel>
  );
}
